import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getGmailStatus, gmailLoginUrl, gmailSync, gmailDisconnect, gmailAutoPoll } from "@/lib/apiClient";
import { useApp } from "@/context/AppContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Mail, RefreshCw, Unplug, Loader2, CheckCircle2, KeyRound, Inbox, Clock, ExternalLink, AlertTriangle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { GmailHealthBanner } from "@/components/GmailHealthBanner";

const ENABLE_API_URL = "https://console.developers.google.com/apis/api/gmail.googleapis.com/overview";
const OAUTH_ERRORS = {
  api_disabled: { title: "Gmail API is not enabled in your Google Cloud project", hint: "Google accepted the login, but the Gmail API is switched off for this project. Enable it, wait 1–2 minutes, then connect again.", link: ENABLE_API_URL, linkText: "Enable Gmail API" },
  access_denied: { title: "Access was denied", hint: "You cancelled or declined the Google permission screen. Connect again and click Allow." },
  insufficient_scope: { title: "Missing Gmail permissions", hint: "Not all requested permissions were granted. Connect again and tick every checkbox on Google's consent screen." },
  code_reused: { title: "Login code already used", hint: "Google only lets a login code be used once. Just click Connect Gmail again." },
  invalid_grant: { title: "Google rejected the login handshake", hint: "The sign-in took too long or was started in another window. Click Connect Gmail again and finish in the window that opens." },
  redirect_mismatch: { title: "Redirect URI mismatch", hint: "The callback URL in D20 doesn't match your Google Cloud OAuth client. Add the redirect URI shown in Settings to the client's Authorized redirect URIs." },
  invalid_client: { title: "Invalid Google client credentials", hint: "GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET don't match your Google Cloud OAuth client." },
  state_expired: { title: "Sign-in session expired", hint: "The login link was older than 10 minutes. Click Connect Gmail to start a fresh one." },
  missing_code: { title: "Google didn't return a login code", hint: "Try again — if it keeps happening, check the OAuth client is of type 'Web application'." },
  token_exchange: { title: "Token exchange failed", hint: "Google returned an unexpected error. Please try again." },
};
const describeError = (reason) => OAUTH_ERRORS[reason] || { title: `Connection failed (${reason})`, hint: "Please try again." };

const Step = ({ n, children }) => (
  <li className="flex gap-2 text-xs text-muted-foreground">
    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-muted font-mono text-[10px] text-foreground">{n}</span>
    <span>{children}</span>
  </li>
);

export const GmailConnectCard = () => {
  const { user, isAdmin } = useApp();
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [popupDone, setPopupDone] = useState(null); // set when this tab is the OAuth landing tab
  const [lastError, setLastError] = useState(null);
  const [reconnectHint, setReconnectHint] = useState(false);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const load = useCallback(() => getGmailStatus().then((s) => {
    setStatus(s);
    const lo = s?.last_oauth;
    if (!s?.connected && lo?.result === "error" && lo.at && Date.now() - new Date(lo.at).getTime() < 15 * 60 * 1000) {
      setLastError({ reason: lo.reason, ...describeError(lo.reason) });
    }
    return s;
  }), []);
  useEffect(() => {
    load();
  }, [load]);

  const finish = useCallback((result, reason) => {
    setWaiting(false);
    if (result === "connected") {
      load().then(() => toast.success("Gmail inbox connected — forwarded invoices will be picked up automatically"));
    } else {
      load();
      const d = describeError(reason);
      setLastError({ reason, ...d });
      toast.error(d.title, { description: d.hint, duration: 10000 });
    }
  }, [load]);

  // Google blocks OAuth inside iframes (returns a bare 403) — open it top-level and poll for completion.
  const connect = () => {
    const url = gmailLoginUrl(user);
    // NOTE: do NOT pass "noopener" — it forces window.open to return null.
    const popup = window.open(url, "d20-gmail-oauth", "popup=yes,width=540,height=680");
    if (!popup) {
      if (window.top !== window.self) {
        toast.error("Your browser blocked the Google window. Open D20 in its own tab, then click Connect Gmail again.");
        return;
      }
      window.location.href = url;
      return;
    }
    setStartedAt(new Date().toISOString());
    setWaiting(true);
    toast.info("Finish signing in to Google in the new window — this page updates automatically.");
  };

  // Poll: Google's COOP header severs the popup link, so status polling is the reliable channel (success AND error).
  useEffect(() => {
    if (!waiting) return;
    let ticks = 0;
    const t = setInterval(async () => {
      ticks += 1;
      const s = await getGmailStatus();
      if (s.connected) {
        finish("connected");
      } else if (s.last_oauth?.result === "error" && startedAt && s.last_oauth.at > startedAt) {
        finish("error", s.last_oauth.reason);
      } else if (ticks > 100) {
        setWaiting(false);
        toast.error("Timed out waiting for Google — click Connect Gmail to try again.");
      }
    }, 3000);
    return () => clearInterval(t);
  }, [waiting, startedAt, finish]);

  // This tab is the OAuth landing page: broadcast the result, try to close, else show a "you can close this" note.
  useEffect(() => {
    const g = params.get("gmail");
    if (!g) return;
    if (g === "reconnect") {
      setParams({});
      setReconnectHint(true);
      setTimeout(() => document.querySelector('[data-testid="gmail-card"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
      return;
    }
    const reason = params.get("reason") || "unknown";
    try {
      const bc = new BroadcastChannel("d20-gmail");
      bc.postMessage({ result: g, reason });
      bc.close();
    } catch (e) { console.debug("BroadcastChannel unavailable, falling back to postMessage", e); }
    if (window.opener && window.opener !== window) {
      try {
        window.opener.postMessage({ type: "d20-gmail", result: g, reason }, window.location.origin);
      } catch (e) { console.debug("Could not notify opener window", e); }
    }
    setPopupDone({ result: g, reason });
    setParams({});
    window.close();
  }, [params, setParams]);

  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin !== window.location.origin || e.data?.type !== "d20-gmail") return;
      finish(e.data.result, e.data.reason);
    };
    window.addEventListener("message", onMsg);
    let bc;
    try {
      bc = new BroadcastChannel("d20-gmail");
      bc.onmessage = (e) => finish(e.data.result, e.data.reason);
    } catch (e) { console.debug("BroadcastChannel unavailable, relying on postMessage", e); }
    return () => {
      window.removeEventListener("message", onMsg);
      bc?.close();
    };
  }, [finish]);

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await gmailSync(user);
      if (!res.ok) toast.error(res.error || "Sync failed");
      else if (res.processed === 0) toast.info(`Inbox checked — nothing new (${res.scanned} scanned, ${res.skipped} skipped)`);
      else toast.success(`Ingested ${res.processed} email(s) → ${res.created.length} invoice(s)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect this Gmail inbox? Already-ingested invoices are kept.")) return;
    await gmailDisconnect(user);
    toast.success("Gmail disconnected");
    load();
  };

  const toggleAuto = async (v) => {
    await gmailAutoPoll(v, user);
    setStatus((s) => ({ ...s, auto_poll: v }));
  };

  if (!status) return null;
  const last = status.last_result;

  if (popupDone) {
    const ok = popupDone.result === "connected";
    const d = ok ? null : describeError(popupDone.reason);
    return (
      <div className={cn("rounded-xl border p-6 text-center", ok ? "border-emerald-500/40 bg-emerald-500/5" : "border-red-500/40 bg-red-500/5")} data-testid="gmail-landing">
        {ok ? <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-500" /> : <Unplug className="h-8 w-8 mx-auto text-red-500" />}
        <div className="font-medium mt-2" data-testid="gmail-landing-title">{ok ? "Gmail connected" : d.title}</div>
        <p className="text-sm text-muted-foreground mt-1" data-testid="gmail-landing-hint">
          {ok ? "You can close this window — D20 has been updated." : d.hint}
        </p>
        {!ok && d.link && (
          <a href={d.link} target="_blank" rel="noreferrer" data-testid="gmail-landing-link"
             className="inline-flex items-center gap-1 mt-3 text-sm text-primary underline underline-offset-4">
            {d.linkText} <ExternalLink className="h-3 w-3" />
          </a>
        )}
        {!ok && <div className="text-[11px] font-mono text-muted-foreground mt-2">code: {popupDone.reason}</div>}
        <Button data-testid="gmail-landing-continue" variant="outline" size="sm" className="mt-3" onClick={() => { if (!ok) setLastError({ reason: popupDone.reason, ...d }); setPopupDone(null); load(); }}>
          Continue here instead
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="gmail-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-500/10 text-red-400">
            <Inbox className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <div className="font-medium flex flex-wrap items-center gap-2">
              Gmail inbox forwarding
              {status.connected && (
                <span data-testid="gmail-connected-badge" className="inline-flex max-w-full items-center gap-1 rounded-md bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-500">
                  <CheckCircle2 className="h-3 w-3 shrink-0" /> <span className="truncate">{status.email}</span>
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">
              Connect a Google inbox and forward supplier invoices to it. D20 checks every {Math.round(status.poll_seconds / 60)} minutes, extracts each attachment,
              codes it, and labels the email <span className="font-mono text-foreground">{status.processed_label}</span>.
            </p>
          </div>
        </div>

        {status.connected && (
          <div className="flex flex-wrap items-center gap-2">
            {(reconnectHint || status.health?.issues?.some((i) => i.action === "reconnect")) && (
              <Button data-testid="gmail-reconnect-btn" size="sm" onClick={connect} disabled={waiting} className={cn(reconnectHint && "ring-2 ring-primary/60 ring-offset-2 ring-offset-background")}>
                {waiting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Mail className="h-4 w-4 mr-1.5" />}
                {waiting ? "Waiting for Google…" : "Reconnect Gmail"}
              </Button>
            )}
            <Button data-testid="gmail-sync-btn" variant="outline" size="sm" onClick={sync} disabled={syncing}>
              {syncing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Check inbox now
            </Button>
            <Button data-testid="gmail-disconnect-btn" variant="ghost" size="sm" onClick={disconnect} disabled={!isAdmin} title={isAdmin ? "" : "Admins only"}>
              <Unplug className="h-4 w-4 mr-1.5" /> Disconnect
            </Button>
          </div>
        )}
        {!status.connected && status.configured && (
          <div className="flex flex-col items-end gap-1">
            <Button data-testid="gmail-connect-btn" onClick={connect} disabled={waiting || !isAdmin} title={isAdmin ? "" : "Ask a workspace admin to connect Gmail"}>
              {waiting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
              {waiting ? "Waiting for Google…" : "Connect Gmail"}
            </Button>
            <span className="text-[11px] text-muted-foreground">Opens Google sign-in in a new tab</span>
          </div>
        )}
        {!status.connected && !status.configured && (
          <span data-testid="gmail-not-configured" className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-500">
            <KeyRound className="h-3.5 w-3.5" /> Add Google credentials to enable
          </span>
        )}
      </div>

      {lastError && !status.connected && (
        <div className="mt-4 flex items-start gap-3 rounded-lg border border-red-500/40 bg-red-500/5 p-3 text-sm" data-testid="gmail-error-banner">
          <AlertTriangle className="h-4 w-4 shrink-0 text-red-500 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-medium" data-testid="gmail-error-title">{lastError.title}</div>
            <p className="text-xs text-muted-foreground mt-0.5" data-testid="gmail-error-hint">{lastError.hint}</p>
            {lastError.link && (
              <a href={lastError.link} target="_blank" rel="noreferrer" data-testid="gmail-error-link"
                 className="inline-flex items-center gap-1 mt-1.5 text-xs text-primary underline underline-offset-4">
                {lastError.linkText} <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
          <button data-testid="gmail-error-dismiss" onClick={() => setLastError(null)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
        </div>
      )}

      {status.connected && <GmailHealthBanner compact onChange={() => {}} />}

      {status.connected ? (
        <div className="mt-4 grid gap-3 sm:grid-cols-3 border-t border-border pt-4 text-sm">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Ingested from Gmail</div>
            <div className="font-mono font-semibold mt-0.5" data-testid="gmail-processed-count">{status.processed_count} invoice(s)</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" /> Last check</div>
            <div className="font-mono text-xs mt-0.5" data-testid="gmail-last-sync">
              {status.last_sync ? new Date(status.last_sync).toLocaleString() : "not yet"}
              {last && <span className={cn("ml-2", last.ok ? "text-emerald-500" : "text-red-500")}>{last.ok ? `+${last.processed}` : "failed"}</span>}
            </div>
          </div>
          <div className="flex items-center justify-between sm:justify-end gap-3">
            <span className="text-xs text-muted-foreground">Auto-check</span>
            <Switch data-testid="gmail-auto-poll" checked={status.auto_poll} onCheckedChange={toggleAuto} />
          </div>
          {last?.created?.length > 0 && (
            <div className="sm:col-span-3 text-xs text-muted-foreground">
              Latest:{" "}
              {last.created.slice(0, 3).map((c) => (
                <button key={c.id} onClick={() => navigate(`/invoice/${c.id}`)} className="text-primary hover:underline mr-2">
                  {c.supplier} {c.invoice_number}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        !status.configured && (
          <ol className="mt-4 space-y-1.5 border-t border-border pt-4" data-testid="gmail-setup-steps">
            <Step n={1}>In Google Cloud Console create a project, enable the <b>Gmail API</b>, and set up an OAuth consent screen (add your Gmail as a test user).</Step>
            <Step n={2}>Create an <b>OAuth client ID</b> (Web application) with redirect URI <span className="font-mono text-foreground break-all">{status.redirect_uri}</span>.</Step>
            <Step n={3}>Put the Client ID and Client Secret into <span className="font-mono text-foreground">GOOGLE_CLIENT_ID</span> / <span className="font-mono text-foreground">GOOGLE_CLIENT_SECRET</span> on the server — the Connect button appears here automatically.</Step>
          </ol>
        )
      )}
    </div>
  );
};
